import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_MEDIA_LIBRARY_ERROR_CODES } from "@lib/shared_constants.json";

export interface PutEngagementAppGroupIdLibraryReplaceFileProps {
  app_group_id: string;
  id: string;
  file: File;
}

export interface EngagementAppGroupIdLibraryReplaceFileResult {
  info: string;
  new_image_asset: {
    id: string;
    created_at: number;
    name: string;
    size: number;
    territory_ids: string[];
    app_group_id: string;
    ext: string | null;
    created_by: string | null;
    last_edited_by: string | null;
    url: string;
    type: string;
    known_width: number | null;
    known_height: number | null;
    ratio: number | null;
    src: string | null;
  };
}

/** The asset type does not support file replacement (e.g. video). */
export type UnsupportedAssetTypeForReplaceError = FetchUserEncodedError<
  SHARED_CONSTANTS_MEDIA_LIBRARY_ERROR_CODES["UNSUPPORTED_ASSET_TYPE_FOR_REPLACE"],
  { asset_type: string }
>;

/** The uploaded file extension does not match the existing asset's extension. */
export type ExtensionMismatchError = FetchUserEncodedError<
  SHARED_CONSTANTS_MEDIA_LIBRARY_ERROR_CODES["EXTENSION_MISMATCH"],
  { expected_extension: string; received_extension: string }
>;

/** The uploaded file could not be processed (likely corrupt). */
export type CorruptFileError = FetchUserEncodedError<
  SHARED_CONSTANTS_MEDIA_LIBRARY_ERROR_CODES["CORRUPT_FILE"],
  { file_name: string | null }
>;

/** The uploaded file exceeds the workspace's size limit. */
export type AssetSizeExceedsLimitError = FetchUserEncodedError<
  SHARED_CONSTANTS_MEDIA_LIBRARY_ERROR_CODES["ASSET_SIZE_EXCEEDS_LIMIT"],
  { size_limit_bytes: number; file_size_bytes: number }
>;

/** Union of all typed errors the replace-file endpoint can return. */
export type ReplaceFileErrors =
  | UnsupportedAssetTypeForReplaceError
  | ExtensionMismatchError
  | CorruptFileError
  | AssetSizeExceedsLimitError;

/**
 * Type guard to narrow a `FetchRequestError` to one of the known replace-file error codes.
 * Use in a `catch` block after calling the replace mutation:
 *
 * ```ts
 * try {
 *   await replaceMediaAssetFile(props);
 * } catch (error) {
 *   if (isReplaceFileError(error as FetchRequestError)) {
 *     // error.errorCode is narrowed to the union of known codes
 *     // error.meta contains typed metadata (e.g. expected_extension, received_extension)
 *   }
 * }
 * ```
 */
export const isReplaceFileError = (error: FetchRequestError): error is ReplaceFileErrors => {
  return error instanceof FetchUserEncodedError;
};
